'use client';

import { isClient } from '@/utils/isClient';
import { Kind } from '@0no-co/graphql.web';
import { persistedOperationHashes } from '@asurion-core/asp-core';
import { invariant } from '@asurion-core/invariant';
import { devtoolsExchange } from '@urql/devtools';
import { persistedExchange } from '@urql/exchange-persisted';
import {
  cacheExchange,
  createClient,
  Exchange,
  fetchExchange,
  makeOperation,
  mapExchange,
  ssrExchange,
  subscriptionExchange,
  UrqlProvider,
} from '@urql/next';
import { DocumentNode } from 'graphql';
import { createClient as createWSClient } from 'graphql-ws';

const getOperationName = (query: DocumentNode): string | undefined => {
  for (const node of query.definitions) {
    if (node.kind === Kind.OPERATION_DEFINITION) {
      return node.name ? node.name.value : undefined;
    }
  }
};

const ssr = ssrExchange();

const exchanges: Exchange[] = [
  devtoolsExchange,
  cacheExchange,
  ssr,
  persistedExchange({
    enforcePersistedQueries: true,
    enableForMutation: true,
    generateHash: async (_, document) => {
      const operationName = getOperationName(document);

      invariant(operationName, 'Operation name is required');
      invariant(
        operationName in persistedOperationHashes,
        'Operation name is not in persistedOperationHashes',
      );

      return persistedOperationHashes[
        operationName as keyof typeof persistedOperationHashes
      ];
    },
  }),
  mapExchange({
    onOperation(operation) {
      const fetchOptions =
        typeof operation.context?.fetchOptions === 'function'
          ? operation.context.fetchOptions()
          : (operation.context?.fetchOptions ?? {});

      return makeOperation(operation.kind, operation, {
        ...operation.context,
        fetchOptions: {
          ...fetchOptions,
          headers: {
            ...fetchOptions.headers,
            'Asurion-Source-App': 'd2c-sales-tool',
            'X-Correlation-ID': crypto.randomUUID(),
          },
        },
      });
    },
  }),
  fetchExchange,
];

if (isClient()) {
  let activeSocket: any, timedOut: NodeJS.Timeout;

  const wsClient = createWSClient({
    url: process.env.NEXT_PUBLIC_WEBSOCKET_CLIENT_URL!,
    keepAlive: 60_000,
    connectionParams: async () => {
      const response = await fetch('/api/token');
      if (!response.ok) {
        throw new Error('Unauthenticated');
      }
      const { token } = await response.json();
      return { token };
    },
    on: {
      connected: (socket) => (activeSocket = socket),
      ping: (received) => {
        if (!received)
          // sent
          timedOut = setTimeout(() => {
            if (activeSocket.readyState === WebSocket.OPEN)
              activeSocket.close(4408, 'Request Timeout');
          }, 5_000); // wait 5 seconds for the pong and then close the connection
      },
      pong: (received) => {
        if (received) clearTimeout(timedOut); // pong is received, clear connection close timeout
      },
    },
  });

  const subExchange = subscriptionExchange({
    forwardSubscription(request) {
      const input = { ...request, query: request.query ?? '' };
      return {
        subscribe(sink) {
          const unsubscribe = wsClient.subscribe(input, sink);
          return { unsubscribe };
        },
      };
    },
  });

  exchanges.push(subExchange);
}

const client = createClient({
  url: isClient()
    ? new URL('/api/graphql', window.location.origin).toString()
    : '#',
  exchanges,
});

export function UrqlBrowserClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <UrqlProvider client={client} ssr={ssr}>
      {children}
    </UrqlProvider>
  );
}

export default UrqlBrowserClientProvider;
